import { format } from 'date-fns'
import { pageIdForPath } from './pageCatalog'
import { decodeDimFilters, decodeArray } from '../../state/urlCodecs'
import { getModelFloorIso } from '../../state/modelFloor'

export interface ViewSnapshot {
  pageId: string | null
  route: string
  /** Today's date (local calendar, YYYY-MM-DD). Always present — anchors relative-time asks. */
  today: string
  kpi?: string
  timeframe?: { preset?: string; from?: string; to?: string }
  /** Campaigns C3 fix-wave #1: the active campaign's raw identity (the `campaign` URL param —
   *  under C3 this is the ONLY campaign state the URL carries; there is no `dim.Topic Level 3`/
   *  BCL param to fall back to). Id only, never a label: serializeView is pure over the URL (no
   *  network calls) so it cannot resolve a display label — the Dataset Overview's campaign list
   *  (already in the system prefix; see datasetOverview.ts) is what lets the model map id→label.
   *  Present iff the URL carries a non-empty `campaign` param; omitted otherwise. */
  campaign?: string
  filters?: Record<string, string[]>
  groupBy?: string[]
  multiTimeframe?: unknown
  /** value_type multi-select (useValueTypeFilterState) — the active NPV/units/… filter. */
  valueTypes?: string[]
  /** Group-by dim promoted to table columns (useColumnsState). */
  columns?: string
  /** Comparison surface (useComparisonState) — present only when a comparison is active. */
  comparison?: { mode: string; compareKpi?: string; from?: string; to?: string }
  /** Contribution decomposition lens (useContributionView) — 'channels' | 'groups'. */
  contributionView?: string
  /** The ≥model-floor cutoff (system.modelFloorIso). Present when the floor has loaded so the
   *  model knows queries before it are clamped up to this date. */
  modelFloor?: string
}

/** Local calendar date as YYYY-MM-DD — the advisor grounds "this year"/"last month" on it. */
export function localTodayIso(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

/**
 * Serialize the current view from a URL pathname + search params into a
 * compact ViewSnapshot the advisor injects each turn so the model always
 * knows what's on screen.
 *
 * Pure over its inputs — no React hooks, no mutation. The model floor is not a
 * URL param; it is read from the global store via the optional `floorIso`
 * argument, which defaults to getModelFloorIso() so the live caller
 * (useAdvisor, at send time) picks up the loaded floor while tests can inject
 * an explicit value.
 */
export function serializeView(
  pathname: string,
  params: URLSearchParams,
  floorIso: string | null = getModelFloorIso(),
  todayIso: string = localTodayIso(),
): ViewSnapshot {
  const snapshot: ViewSnapshot = {
    pageId: pageIdForPath(pathname),
    route: pathname,
    today: todayIso,
  }

  // KPI: reassemble from kpi_root / kpi_driver / kpi_value_type
  const kpiRoot = params.get('kpi_root')
  if (kpiRoot !== null) {
    const kpiDriver = params.get('kpi_driver')
    const kpiValueType = params.get('kpi_value_type')
    if (kpiDriver && kpiValueType) {
      snapshot.kpi = `${kpiRoot}__${kpiDriver}__${kpiValueType}`
    } else {
      snapshot.kpi = kpiRoot
    }
  }

  // Timeframe: build from tf_preset / from / to; omit if none present
  const tfPreset = params.get('tf_preset')
  const tfFrom = params.get('from')
  const tfTo = params.get('to')
  if (tfPreset !== null || tfFrom !== null || tfTo !== null) {
    const timeframe: { preset?: string; from?: string; to?: string } = {}
    if (tfPreset !== null) timeframe.preset = tfPreset
    if (tfFrom !== null) timeframe.from = tfFrom
    if (tfTo !== null) timeframe.to = tfTo
    snapshot.timeframe = timeframe
  }

  // Campaigns C3 fix-wave #1 (CRITICAL): surface the active campaign's identity. Under C3 the URL
  // carries ONLY `campaign=<id>` (no more materialized dim.Topic Level 3/BCL params to read) — a
  // prior version of this function read those legacy dim params and never the id, so with a
  // campaign active the snapshot showed a bare custom window while the screen showed
  // campaign-filtered data (the advisor would quote window-only figures). Id only — see the
  // ViewSnapshot.campaign doc comment for why no label.
  const campaignId = params.get('campaign')
  if (campaignId) {
    snapshot.campaign = campaignId
  }

  // Dim filters: build flat record from all params, then decode
  const flatParams: Record<string, string> = {}
  for (const [key, value] of params.entries()) {
    flatParams[key] = value
  }
  const filters = decodeDimFilters(flatParams)
  if (Object.keys(filters).length > 0) {
    snapshot.filters = filters
  }

  // Group-by: comma-joined; omit if empty
  const groupby = decodeArray(params.get('groupby'))
  if (groupby.length > 0) {
    snapshot.groupBy = groupby
  }

  // Multi-timeframe: JSON string; best-effort parse; omit on failure or absence
  const tfList = params.get('tf_list')
  if (tfList !== null) {
    try {
      snapshot.multiTimeframe = JSON.parse(tfList)
    } catch {
      // Omit multiTimeframe on parse failure — defensive, no silent runtime error
    }
  }

  // Value-type filter: comma-list, same codec as useValueTypeFilterState; omit if empty
  const valueTypes = decodeArray(params.get('value_types'))
  if (valueTypes.length > 0) {
    snapshot.valueTypes = valueTypes
  }

  // Columns: a single group-by dim promoted to table columns; omit if absent
  const columns = params.get('columns')
  if (columns) {
    snapshot.columns = columns
  }

  // Comparison surface: mirror useComparisonState's mode derivation
  //   compareMode (period|metric) wins; else legacy ?compare=true → period; else off.
  // Only surface an ACTIVE comparison, and only the fields that mode uses:
  // compareKpi for metric, compareFrom/compareTo for period (defaults are derived at
  // runtime and not in the URL — we report only what the URL carries, never fabricated).
  const compareModeRaw = params.get('compareMode')
  const validMode =
    compareModeRaw === 'period' || compareModeRaw === 'metric' ? compareModeRaw : null
  const effectiveMode = validMode ?? (params.get('compare') === 'true' ? 'period' : 'off')
  if (effectiveMode !== 'off') {
    const comparison: { mode: string; compareKpi?: string; from?: string; to?: string } = {
      mode: effectiveMode,
    }
    if (effectiveMode === 'metric') {
      const compareKpi = params.get('compareKpi')
      if (compareKpi) comparison.compareKpi = compareKpi
    } else {
      const compareFrom = params.get('compareFrom')
      const compareTo = params.get('compareTo')
      if (compareFrom) comparison.from = compareFrom
      if (compareTo) comparison.to = compareTo
    }
    snapshot.comparison = comparison
  }

  // Contribution decomposition lens: only when the URL carries a valid view value
  const viewRaw = params.get('view')
  if (viewRaw === 'channels' || viewRaw === 'groups') {
    snapshot.contributionView = viewRaw
  }

  // Model floor: surface it (when loaded) so the model knows the ≥floor cutoff exists
  if (floorIso) {
    snapshot.modelFloor = floorIso
  }

  return snapshot
}
